// src/pages/Dashboard/Admin/Votes/components/VoteFormDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { FaCalendarAlt, FaSave, FaTimes, FaVoteYea } from "react-icons/fa";

import {
  VOTE_TYPES,
  getDefaultVoteForm,
  mapVoteToForm,
  validateVoteForm,
} from "../utils/votesDashboardUtils";

function DrawerField({ label, children, hint, error }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      {children}

      {error ? (
        <p className="mt-1 text-xs leading-5 text-rose-600 dark:text-rose-300">
          {error}
        </p>
      ) : hint ? (
        <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export default function VoteFormDrawer({
  isOpen,
  mode,
  vote,
  submitting,
  onClose,
  onSubmit,
}) {
  const [formData, setFormData] = useState(getDefaultVoteForm());

  useEffect(() => {
    if (!isOpen) return;

    setFormData(mode === "edit" ? mapVoteToForm(vote) : getDefaultVoteForm());
  }, [isOpen, mode, vote]);

  const errors = useMemo(() => validateVoteForm(formData), [formData]);
  const canSubmit = Object.keys(errors).length === 0 && !submitting;

  const title =
    mode === "edit" ? "Modifier un vote / sondage" : "Ajouter un vote / sondage";

  const submitLabel =
    mode === "edit" ? "Enregistrer les modifications" : "Créer le vote";

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    onSubmit?.(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9998]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Configuration
            </p>

            <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
              <FaVoteYea className="text-indigo-600 dark:text-indigo-300" />
              {title}
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Définissez le titre, la description, la période d’ouverture, le
              type et l’activation du vote. Les questions se gèrent ensuite
              depuis le tableau.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
            <DrawerField label="Titre *" error={errors.title}>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                maxLength={120}
                placeholder="Ex : Élection du bureau 2026"
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
              />
            </DrawerField>

            <DrawerField label="Description">
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                maxLength={1000}
                rows={5}
                placeholder="Consignes, contexte, informations utiles pour les adhérents..."
                className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
              />

              <p className="mt-1 text-right text-[11px] text-slate-500 dark:text-slate-400">
                {formData.description.length}/1000
              </p>
            </DrawerField>

            <div className="grid gap-4 sm:grid-cols-2">
              <DrawerField label="Type">
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  {VOTE_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </DrawerField>

              <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={!!formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
                />

                <span>
                  <span className="block text-sm font-bold text-slate-900 dark:text-white">
                    Vote actif
                  </span>

                  <span className="mt-1 block text-xs leading-5 text-slate-500 dark:text-slate-400">
                    Un vote inactif ne sera pas utilisable côté adhérent, même
                    pendant sa période d’ouverture.
                  </span>
                </span>
              </label>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
              <p className="mb-4 flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                <FaCalendarAlt className="text-indigo-600 dark:text-indigo-300" />
                Période d’ouverture
              </p>

              <div className="grid gap-4 sm:grid-cols-2">
                <DrawerField label="Début *" error={errors.start_time}>
                  <input
                    type="datetime-local"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  />
                </DrawerField>

                <DrawerField
                  label="Fin *"
                  error={errors.end_time || errors.range}
                >
                  <input
                    type="datetime-local"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleChange}
                    min={formData.start_time || undefined}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  />
                </DrawerField>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
              >
                <FaSave />
                {submitting ? "Enregistrement..." : submitLabel}
              </button>
            </div>
          </div>
        </form>
      </aside>
    </div>
  );
}